import { of as observableOf, Observable, Subject, forkJoin } from 'rxjs'
import { Component, ViewChild, OnInit } from '@angular/core'
import { mergeMap, takeUntil } from 'rxjs/operators'
import {
  IProvider,
  ProviderODataService,
  ProviderAddressODataService,
  defaultTimezone,
  PhoneNumberODataService,
  Permissions
} from '@exchange/domain/.'
import { ActivatedRoute, Router } from '@angular/router'
import { ProvidersEditDemographicsComponent } from '../providers-edit-demographics/providers-edit-demographics.component'
import { ProvidersEditContactsComponent } from '../providers-edit-contacts/providers-edit-contacts.component'
import { ProvidersEditEdiConfigComponent } from '../providers-edit-edi-config/providers-edit-edi-config.component'
import { ToastrService } from 'ngx-toastr'
import { UtilService } from '@exchange/app/services'
import {
  BasicUserPermissionsComponentModel,
  selectBasicUserPermissionsComponentModel
} from '@exchange/app/store/selectors/selectors'
import { select, Store } from '@ngrx/store'
import { AppState } from '@exchange/app/store/models/app-state'
import { ProvidersEditCoveragesComponent } from '../providers-edit-coverages/providers-edit-coverages.component'
import { PagedResult } from 'common-services'

@Component({
  selector: 'app-providers-edit',
  templateUrl: './providers-edit.component.html',
  styleUrls: ['./providers-edit.component.scss'],
  standalone: false
})
export class ProvidersEditComponent implements OnInit {
  @ViewChild('demographics', { static: true })
  demographics: ProvidersEditDemographicsComponent
  @ViewChild('contacts')
  contacts: ProvidersEditContactsComponent
  @ViewChild('ediConfigurations')
  ediConfigurations: ProvidersEditEdiConfigComponent
  @ViewChild('coverages', { static: true })
  coverages: ProvidersEditCoveragesComponent

  componentModel: BasicUserPermissionsComponentModel
  ngUnsubscribe: Subject<boolean> = new Subject()

  model: IProvider = {} as IProvider
  busy: any = null
  selectedTab = 'Demographics'
  formTitle: string
  tabSettings = {
    Demographics: {
      title: () => {
        let title = this.model.Id ? 'Edit Facility' : 'Add Facility'
        if (this.hasSystemManagementPermission()) {
          title += ' or Agency'
        }
        return title
      },
      showSave: true,
      validate: () => this.demographics.validate(),
      save: () => this.demographics.save()
    },
    Contacts: {
      title: () => 'Edit Facility or Agency - Contacts',
      showSave: false
    },
    EdiConfigurations: {
      title: () => 'Edit Facility or Agency - 835 EDI Configurations',
      showSave: true,
      validate: () => this.ediConfigurations.validate(),
      save: () => this.ediConfigurations.save()
    },
    // May need to adjust this still
    Coverages: {
      title: () => 'Edit Facility or Agency - Coverages',
      showSave: false
    }
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private odata: ProviderODataService,
    private providerAddressService: ProviderAddressODataService,
    private phoneNumberService: PhoneNumberODataService,
    private toastr: ToastrService,
    private util: UtilService,
    private store: Store<AppState>
  ) {
    this.store
      .pipe(select(selectBasicUserPermissionsComponentModel), takeUntil(this.ngUnsubscribe))
      .subscribe(m => {
        this.componentModel = m
      })

    this.route.params.subscribe(params => {
      const id = +params['id']

      if (id) {
        this.busy = this.odata
          .query()
          .filter(`Id eq ${id}`)
          .expand('Market')
          .exec()
          .subscribe(response => {
            this.busy = null
            if (response.data.length) {
              const provider = response.data[0]
              this.formTitle = this.getFormTitle()
              if (provider) {
                const observables: Observable<any>[] = [
                  this.providerAddressService
                    .query()
                    .filter(`Id eq ${provider.MailingAddressId}`)
                    .exec(),
                  this.providerAddressService
                    .query()
                    .filter(`Id eq ${provider.PhysicalAddressId}`)
                    .exec(),
                  this.phoneNumberService
                    .query()
                    .filter(`Id eq ${provider.PhoneNumber1Id}`)
                    .exec(),
                  this.phoneNumberService
                    .query()
                    .filter(`Id eq ${provider.OrganizationPhoneNumberOverride1Id}`)
                    .exec()
                ]

                if (provider.LocationCoverageAddressId) {
                  observables.push(
                    this.providerAddressService
                      .query()
                      .filter(`Id eq ${provider.LocationCoverageAddressId}`)
                      .exec()
                  )
                } else {
                  observables.push(observableOf(null))
                }

                forkJoin(observables).subscribe(
                  ([
                    mailingAddress,
                    physicalAddress,
                    phoneNumber1,
                    phoneNumberOverride,
                    locationCoverageAddress
                  ]) => {
                    provider.MailingAddress = mailingAddress.data[0]
                    provider.PhysicalAddress = physicalAddress.data[0]
                    provider.PhoneNumber1 = phoneNumber1.data[0]
                    provider.OrganizationPhoneNumberOverride1 = phoneNumberOverride.data[0]
                    if (locationCoverageAddress) {
                      provider.LocationCoverageAddress = locationCoverageAddress.data[0]
                    }
                    this.model = provider
                  }
                )
              }
            } else {
              this.backToList()
            }
          }, this.showError.bind(this))
      } else {
        this.initializeAdd()
      }
    })
  }

  ngOnInit(): void {
    this.formTitle = this.getFormTitle()
  }

  initializeAdd() {
    const provider = {
      Id: 0,
      Name: '',
      MailingAddressId: null,
      PhysicalAddressId: null,
      LocationCoverageAddressId: null,
      PhysicalAddress: { CountryId: 1 },
      MailingAddress: { CountryId: 1 },
      EmailAddress: null,
      PhoneNumber: null,
      PhoneNumber1Id: null,
      PhoneNumber1: {},
      OrganizationPhoneNumberOverride: null,
      OrganizationPhoneNumberOverride1Id: null,
      OrganizationPhoneNumberOverride1: {},
      Npi: null,
      TaxIdNumber: null,
      ProviderTypeId: null,
      ProviderType: null,
      IsAnchor: false,
      IanaTimeZone: defaultTimezone,
      PayeeAddressConfiguration: {},
      PayerAddressConfiguration: {},
      Local911PhoneNumber: null,
      Local911PhoneNumber1: {},
      Local911PhoneNumber1Id: null
    } as IProvider
    const paged = {
      skipped: 0,
      rowCount: 1,
      data: [provider]
    } as PagedResult<IProvider>

    return observableOf(paged)
  }

  initializeEdit() {
    return this.initializeModel()
  }

  private initializeModel(): Observable<PagedResult<IProvider>> {
    return this.route.params.pipe(
      mergeMap(params => {
        const providerId = +params['id']
        return this.odata
          .query()
          .filter(`Id eq ${providerId}`)
          .exec()
      })
    )
  }

  backToList(successMessage: string = null) {
    this.router.navigate(['/system', 'providers'], {
      queryParams: { successMessage }
    })
  }

  getFormTitle() {
    return this.tabSettings[this.selectedTab].title()
  }

  onTabSelect(tabName: string) {
    this.selectedTab = tabName
    this.formTitle = this.getFormTitle()
    // Fire off auto size on tab select due to AfterViewInit lifecycle step
    // not adhering to the request
    if (tabName === 'Coverages') {
      this.coverages.autoSizeGridColumns()
    }
  }

  onSubmit(): boolean {
    const selectedTab = this.tabSettings[this.selectedTab]

    if (selectedTab && selectedTab.validate) {
      const errors = selectedTab.validate()

      this.toastr.clear()
      if (errors.length > 0) {
        this.toastr.error(errors.join('<br />'))
        return
      }
    }

    if (selectedTab.showSave) {
      selectedTab.save().subscribe(() => {
        this.showSuccess('Provider was saved successfully.')
      }, this.showError.bind(this))
    }
  }

  showSuccess(successMessage: string): void {
    this.busy = null
    this.backToList(successMessage)
  }

  showError(response): void {
    this.busy = null

    const msg: string = this.util.getErrorString(response)
    this.toastr.error(msg)
  }

  hasSystemManagementPermission(): boolean {
    return this.componentModel.userPermissions.includes(Permissions.SystemManagement)
  }
}
